const EDITOR_TAG = /<\/?(p|br|div|span|strong|em|b|i|u|s|code|pre|blockquote|h[1-6]|ul|ol|li|a|mark|sub|sup)\b[^>]*>/gi;

/** What an editor leaves behind once everything is deleted (`<p></p>`). */
function isEmptyMarkup(value: string): boolean {
  const trimmed = value.trim();
  return trimmed.startsWith("<") && trimmed.replace(EDITOR_TAG, "").replace(/&nbsp;|\s/gi, "") === "";
}

/** Whether a stored (DTO-space) custom-field value carries nothing.
 *
 *  Absent, `null`, a whitespace-only string, emptied rich text, an empty
 *  array and an object whose every member is blank all read as the same
 *  state — the field shows "—". `0` and `false` are values (a $0 deductible, a deliberate "no"). */
export function isCustomFieldValueBlank(value: unknown): boolean {
  if (value == null) return true;
  if (typeof value === "string") return value.trim() === "" || isEmptyMarkup(value);
  if (Array.isArray(value)) return value.length === 0;
  if (value instanceof Date) return false;
  if (typeof value === "object") {
    return Object.values(value as Record<string, unknown>).every(
      isCustomFieldValueBlank,
    );
  }
  return false;
}

/** Whether two stored custom-field values name the same state. Two blanks are
 *  equal whatever their spelling (`null`, `""`, `[]`, an absent key); a Date
 *  equals the ISO string of the same instant. Every writer that turns a
 *  no-op edit into a write — a blur that typed nothing, a reset to the value
 *  already held — gates on this so nothing is stored and no activity says a
 *  value changed. */
export function customFieldValuesEqual(before: unknown, after: unknown): boolean {
  const beforeBlank = isCustomFieldValueBlank(before);
  const afterBlank = isCustomFieldValueBlank(after);
  if (beforeBlank || afterBlank) return beforeBlank && afterBlank;
  return JSON.stringify(before) === JSON.stringify(after);
}
